import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { OrganizationSchema, WebPageSchema, ServiceSchema, LocalBusinessSchema, FAQSchema } from '../components/SEOStructuredData';
import { Container, Title, Text, Button, SimpleGrid, Image, Badge, List, ThemeIcon, Group, Card } from '@mantine/core';
import { motion } from 'framer-motion';
import { 
  IconCheck,
  IconArrowRight,
  IconShieldLock,
  IconCode,
  IconBuilding,
  IconShieldCheck,
  IconRocket,
  IconUsers
} from '@tabler/icons-react';
import classes from './Home.module.css';

export default function Home() {
  // Define FAQs for structured data
  const faqs = [
    {
      question: "What process automation solutions does Optifyne offer?",
      answer: "Optifyne offers secure process automation solutions using Microsoft Power Platform and Nintex K2, specialising in solutions for government and enterprise organisations with security requirements."
    },
    {
      question: "Does Optifyne work with government and regulated organisations?",
      answer: "Yes, Optifyne has UK-based developers with 15+ years of experience working with government departments, agencies, and highly regulated organisations."
    },
    {
      question: "What technologies does Optifyne specialise in?",
      answer: "Optifyne specialises in Microsoft Power Platform (Power Apps, Power Automate, Power BI, Power Pages), Copilot Studio, AI Builder, Dataverse, UiPath and Nintex K2 for process automation and integration solutions."
    },
    {
      question: "Can Optifyne integrate with existing systems?",
      answer: "Yes, Optifyne provides seamless integration with platforms like Microsoft Exchange, Active Directory, SAP, SQL Server, SharePoint and Power BI—whether cloud-hosted or on-premise."
    },
    {
      question: "Does Optifyne offer training and support?",
      answer: "Yes, Optifyne offers training, coaching and support services to upskill teams and ensure successful adoption, knowledge transfer, and long-term success of automation solutions."
    }
  ];
  
  // Define services for structured data
  const services = [
    {
      name: 'Process Automation',
      description: 'Enterprise-grade process automation solutions using Microsoft Power Platform and Nintex K2.',
      url: 'https://optifyne.com/#services'
    },
    {
      name: 'Security Solutions',
      description: 'Security-focused automation and integration solutions for government and enterprise organisations.',
      url: 'https://optifyne.com/#security'
    },
    {
      name: 'Government Solutions',
      description: 'Specialised automation solutions for government departments and agencies in highly regulated environments.',
      url: 'https://optifyne.com/#government'
    }
  ];
  const technologies = [
    { name: 'Power Platform', image: '/images/PowerPlatform_scalable.svg' },
    { name: 'Power Apps', image: '/images/PowerApps_scalable.svg' },
    { name: 'Power Automate', image: '/images/PowerAutomate_scalable.svg' },
    { name: 'UiPath', image: '/images/uip-logo.png' },
    { name: 'Power BI', image: '/images/PowerBI_scalable.svg' },
    { name: 'Power Pages', image: '/images/PowerPages_scalable.svg' },
    { name: 'Copilot Studio', image: '/images/CopilotStudio_scalable.svg' },
    { name: 'AI Builder', image: '/images/AIBuilder_scalable.svg' },
    { name: 'Dataverse', image: '/images/Dataverse_scalable.svg' },
    { name: 'Nintex', image: '/images/nintex_logo.svg' }

  ];

  const features = [
    {
      title: 'Highly regulated environments',
      icon: 'shield-lock',
      highlight: 'Trusted Professionals',
      description: 'Experienced professionals with proven expertise working in highly regulated government and private sector environments.'
    },
    {
      title: 'Technical excellence',
      icon: 'code',
      highlight: '15+ Years Experience',
      description: 'Deep expertise in Nintex K2, building low-code/ no-code applications. Extensive expertise in designing and developing solutions using the Microsoft Power Platform, including Power Apps, Power Automate, and Power BI.'
    },
    {
      title: 'Government & private sector focus',
      icon: 'building-government',
      highlight: 'Public Sector Expertise',
      description: 'Proven track record delivering process automation for government departments, public agencies, and private organisations across industries such as finance, healthcare, and energy.'
    },
    {
      title: 'Security-first approach',
      icon: 'shield-check',
      highlight: 'ISO 27001 Compliant',
      description: 'We ensure compliance, data protection, and governance are at the heart of every solution.'
    },
    {
      title: 'Agile and results-driven',
      icon: 'rocket',
      highlight: 'Agile Approach',
      description: 'Our agile delivery approach means faster results with minimal disruption and maximum impact.'
    },
    {
      title: 'Ongoing support',
      icon: 'users',
      highlight: '24/7 Support',
      description: 'We provide ongoing support, training, and optimisation for your K2 solutions.'
    }
  ];

  return (
    <>
      {/* SEO Structured Data */}
      <Helmet>
        <title>Optifyne - Simplifying Complexity, Amplifying Results</title>
        <meta name="description" content="Optifyne delivers secure process automation for government and enterprise. UK-based developers with 15+ years of experience in highly regulated environments." />
      </Helmet>
      <OrganizationSchema />
      <LocalBusinessSchema />
      <WebPageSchema 
        title="Optifyne - Simplifying Complexity, Amplifying Results"
        description="Secure process automation solutions by trusted experts for government and enterprise. Specialists in Nintex K2 and Microsoft Power Platform."
        url="https://optifyne.com/"
      />
      <FAQSchema questions={faqs} />
      {services.map((service, index) => (
        <ServiceSchema key={index} name={service.name} description={service.description} url={service.url} />
      ))}
    <div className={classes.wrapper}>
      {/* Hero Section */}
      <section className={classes.hero}>
        {/* Mobile Hero Banner - Always visible at the top on mobile */}
        <div className={classes.mobileHeroBanner}>
          <div className={classes.titleWrapper}>
            <Title className={classes.title} order={1}>
              <span className={classes.highlight}>Secure Process Automation</span>
            </Title>
            <Title className={classes.title} order={1}>
              for Government & Enterprise
            </Title>
          </div>
          <List
            spacing="xs"
            size="sm"
            icon={
              <ThemeIcon size={16} radius="xl" color="blue">
                <IconCheck size={10} />
              </ThemeIcon>
            }
          >
            <List.Item>UK based developers experienced in regulated environments</List.Item>
            <List.Item>15+ years K2 experience</List.Item>
            <List.Item>Industry-wide focus</List.Item>
          </List>
          
          <Text
            className={classes.tagline}
            mt="sm"
            fw={600}
            fs="italic"
            c="dimmed"
          >
            Simplifying Complexity, Amplifying Results
          </Text>
          
          <Button
            component={Link}
            to="/contact"
            size="md"
            className={classes.primaryButton}
            rightSection={<IconArrowRight size={16} />}
            mt="md"
          >
            Schedule a Consultation
          </Button>
        </div>
        
        {/* Desktop Hero Content */}
        <Container size="xl" className={classes.desktopHeroContainer}>
          <div className={classes.heroInner}>
            <motion.div
              className={classes.heroContent}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className={classes.titleWrapper}>
                <Title className={classes.title} order={1}>
                  <span className={classes.highlight}>Secure Process Automation</span>
                </Title>
                <Title className={classes.title} order={1}>
                  for Government & Enterprise
                </Title>
              </div>
              <List
                spacing="xs"
                size="md"
                icon={
                  <ThemeIcon size={18} radius="xl" color="blue">
                    <IconCheck size={12} />
                  </ThemeIcon>
                }
              >
                <List.Item>UK based developers experienced in regulated environments</List.Item>
                <List.Item>15+ years K2 experience</List.Item>
                <List.Item>Industry-wide focus</List.Item>
              </List>
              
              <div className={classes.certificationBadge}>
                <iframe 
                  src="https://registry.blockmarktech.com/certificates/6af16f19-2fb9-442f-b42d-9a6f7a9d3e70/widget/?tooltip_position=top_right&theme=transparent&hover=t" 
                  style={{ border: 'none', height: '90px', width: '90px' }}
                  title="Cyber Essentials Certification"
                  aria-label="Cyber Essentials Certified"
                />
              </div>
              
              <Text
                className={classes.tagline}
                mt="md"
                fw={600}
                fs="italic"
                c="dimmed"
                size="lg"
              >
                Simplifying Complexity, Amplifying Results
              </Text>
              
              <Button
                component={Link}
                to="/contact"
                size="md"
                className={classes.primaryButton}
                rightSection={<IconArrowRight size={16} />}
                mt="lg"
              >
                Schedule a Consultation
              </Button>
            </motion.div>

            <motion.div 
              className={classes.heroImage}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className={classes.heroImageWrapper}>
                <Image
                  src="/images/process_automation.jpeg"
                  alt="Secure Process Automation"
                  height={300}
                  width={400}
                  fit="cover"
                  radius={12}
                  className={classes.processImage}
                  style={{
                    width: '400px',
                    height: '300px',
                    minWidth: '400px',
                    minHeight: '300px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    objectFit: 'cover'
                  }}
                  fallbackSrc="https://placehold.co/400x300?text=Secure+Process+Automation"
                />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>
      {/* Technology Stack Section */}
      <motion.section 
        className={classes.heroCarousel}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <Container size="xl">
          <Text 
            size="sm" 
            c="dimmed" 
            mb="md" 
            ta="center"
            style={{ 
              letterSpacing: '0.5px',
              fontWeight: 500
            }}
          >
            Powered by Industry-Leading Technologies
          </Text>
        </Container>
        <div className={classes.scrollContainer}>
          <motion.div 
            className={classes.scrollContent}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {[...technologies, ...technologies, ...technologies].map((tech, index) => (
              <motion.div
                key={`${tech.name}-${index}`}
                className={classes.heroTechCard}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
              >
                <Image
                  src={tech.image}
                  alt={`${tech.name} - Optifyne Technology Partner`}
                  width={120}
                  height={30}
                  fit="contain"
                  style={{ maxWidth: '40%', opacity: 0.85 }}
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>


      {/* Why Choose Us Section */}
      <section className={classes.whyChooseSection}>
        <Container size="xl" px="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Title order={2} className={classes.sectionTitle} ta="center" mb="sm">
              Why Choose Optifyne?
            </Title>
            <div className={classes.subtitleWrapper}>
              <Text size="lg" c="dimmed">
                Trusted by government departments and enterprises for secure process automation
              </Text>
            </div>
          </motion.div>

          <SimpleGrid
            mt={50}
            cols={{ base: 1, sm: 2, lg: 3 }}
            spacing="xl"
            verticalSpacing="xl"
          >
            {features.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
              >
                <Card className={classes.featureCard}>
                  <div className={classes.cardHeader}>
                    <ThemeIcon size={48} radius="md" className={classes.cardIcon}>
                      {item.icon === 'shield-lock' && <IconShieldLock size={24} />}
                      {item.icon === 'code' && <IconCode size={24} />}
                      {item.icon === 'building-government' && <IconBuilding size={24} />}
                      {item.icon === 'shield-check' && <IconShieldCheck size={24} />}
                      {item.icon === 'rocket' && <IconRocket size={24} />}
                      {item.icon === 'users' && <IconUsers size={24} />}
                    </ThemeIcon>
                    
                    <Title order={3} className={classes.cardTitle}>
                      {item.title}
                    </Title>
                  </div>
                  
                  <Badge className={classes.cardBadge}>
                    {item.highlight}
                  </Badge>
                  
                  <Text className={classes.cardDescription}>
                    {item.description}
                  </Text>
                </Card>
              </motion.div>
            ))}
          </SimpleGrid>
        </Container>
      </section>

      {/* Services Section */}
      <section className={classes.servicesSection}>
        <Container size="xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Title order={2} className={classes.sectionTitle} ta="center" mb="sm">
              Our Services
            </Title>
            <Text size="lg" c="dimmed" className={classes.sectionDescription} ta="center">
              Secure process automation solutions for government and enterprise
            </Text>
          </motion.div>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={40} mt={50}>
            {[
              {
                title: 'Process automation and optimisation',
                description: 'Eliminating inefficiencies, streamline processes, and increase productivity for both public and private organisations.',
                Icon: IconRocket,
                gradient: ['#6B46C1', '#3182CE']
              },
              {
                title: 'Legacy system integration',
                description: 'Seamless integration with platforms like Microsoft Exchange, Active Directory, SAP, SQL Server, SharePoint and Power BI—whether cloud-hosted or on-premise.',
                Icon: IconCode,
                gradient: ['#2563EB', '#0EA5E9']
              },
              {
                title: 'Center of Excellence (CoE) setup',
                description: 'Establishing governance frameworks for process standardisation, best practices, and continuous improvement.',
                Icon: IconShieldCheck,
                gradient: ['#7C3AED', '#3B82F6']
              },
              {
                title: 'Training, coaching and support',
                description: 'Upskilling teams to ensure successful adoption, knowledge transfer, and long-term success.',
                Icon: IconUsers,
                gradient: ['#8B5CF6', '#2563EB']
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={classes.serviceCard}
              >
                <Card p="xl" className={classes.serviceContent}>
                  <div className={classes.iconWrapper}>
                    <ThemeIcon
                      size={56}
                      radius="xl"
                      className={classes.serviceIcon}
                      variant="light"
                    >
                      <service.Icon size={28} stroke={1.5} />
                    </ThemeIcon>
                  </div>
                  <Title order={3} className={classes.serviceTitle} mt="md">
                    {service.title}
                  </Title>
                  <Text className={classes.serviceDescription} mt="sm">
                    {service.description}
                  </Text>
                </Card>
              </motion.div>
            ))}
          </SimpleGrid>
        </Container>
      </section>

      {/* CTA Section */}
      <section className={classes.ctaSection}>
        <Container size="xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={classes.ctaContent}
          >
            <Title order={2} className={classes.sectionTitle} ta="center">
              Choose Your <span className={classes.highlight}>Engagement Model</span>
            </Title>
            
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={40} mt={40}>
              <Card className={classes.ctaCard}>
                <Title order={3} className={classes.ctaCardTitle}>
                  End-to-End Delivery
                </Title>
                <Text className={classes.ctaCardText}>
                  Full project implementation from planning to deployment, with our trusted experts handling every aspect of your automation needs.
                </Text>
              </Card>
              <Card className={classes.ctaCard}>
                <Title order={3} className={classes.ctaCardTitle}>
                  Team Augmentation
                </Title>
                <Text className={classes.ctaCardText}>
                  Enhance your existing team with our skilled professionals, providing expertise and support where you need it most.
                </Text>
              </Card>
            </SimpleGrid>
            
            <Group justify="center" mt={48}>
              <motion.div 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                style={{ display: 'inline-block' }}
              >
                <Button
                  component={Link}
                  to="/contact"
                  size="xl"
                  className={classes.ctaButton}
                  rightSection={<IconArrowRight size={20} />}
                >
                  Start Your Journey
                </Button>
              </motion.div>
            </Group>
          </motion.div>
        </Container>
      </section>
    </div>
    </>
  );
}
